import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Divider, Grid, Stack } from '@mui/material';

import { ICustodyCheckoutReq, VendorApi } from '@/api';
import { RenderFormField, SelectItem } from '@/components';
import { IVendorType } from '@/models/vendors';
import { uploadSignatureFile } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { useUserStore } from '@/store/store.user';

import { CustodyActionSection } from './CustodyActionSection';
import { CustodyCheckoutCourtFormFields } from './data';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import {
  getCustodyActionDefaultValues,
  normalizeCustodyActionDateTime,
  useCustodyOfficerOptions,
} from './useCustodyActionForm';
import { CustodyActions } from '../../data';
import { useKasePropertyContext } from '../../PropertyContext';

const DefaultCheckoutToCourtValues = {
  actionDateTime: '',
  expectedBackDate: '',
  notes: '',
  officer: '',
  signature: '',
  vendor: '',
  trackingNumber: '',
};

interface ToCourtFormProps {
  custodyAction: string;
}

export const CheckoutForm = (props: ToCourtFormProps) => {
  const { custodyAction } = props;
  const { currentPropertyId, custodyModalValues, parentId, sourceAdapter } =
    useKasePropertyContext();

  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const [vendorTypeIds, setVendorTypeIds] = useState<IVendorType[]>([]);
  const [courtVendors, setCourtVendors] = useState<SelectItem[]>([]);
  const { officerOptions } = useCustodyOfficerOptions();
  const methods = useForm<ICustodyCheckoutReq>({
    defaultValues: {
      ...DefaultCheckoutToCourtValues,
      ...getCustodyActionDefaultValues(user),
    },
    mode: 'all',
  });
  const { getValues, trigger } = methods;

  const isCheckoutToCourtForm = custodyAction === CustodyActions.TO_COURT;

  useEffect(() => {
    fetchVendorTypes();
  }, [custodyAction]);

  useEffect(() => {
    if (vendorTypeIds) {
      fetchCourtVendors();
    }
  }, [vendorTypeIds]);

  const fetchVendorTypes = async () => {
    try {
      const res = await VendorApi.getTypes();
      const vendorTypeName = isCheckoutToCourtForm ? 'court' : 'lab';
      const matchedVendorTypes = res.data.filter(({ name }) =>
        isCheckoutToCourtForm
          ? name.toLowerCase() === vendorTypeName
          : name.toLowerCase().includes(vendorTypeName),
      );

      if (matchedVendorTypes?.length) setVendorTypeIds(matchedVendorTypes);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const fetchCourtVendors = async () => {
    try {
      const filter = JSON.stringify({
        type: [...vendorTypeIds],
        isActive: true,
      });
      const res = await VendorApi.list({
        limit: 1000,
        filter,
      });
      setCourtVendors(
        res.data.results.map(({ _id, name }) => ({
          label: name,
          value: String(_id),
        })),
      );
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onCheckout = async () => {
    const isValid = await trigger();
    const { bulkSelection } = custodyModalValues;
    // If bulkSelection is not undefined, it means user wants to Checkout multiple selected Properties
    const properties =
      bulkSelection ?? (currentPropertyId ? [currentPropertyId] : []);
    if (isValid && parentId && properties) {
      const values = getValues();
      values.actionDateTime = normalizeCustodyActionDateTime(
        values.actionDateTime,
      ) as string;

      if (values.signature) {
        const signatureFile = await uploadSignatureFile(
          values.signature as string,
          'checkout-property-signature.png',
        );

        if (signatureFile?.length) {
          values.signature = signatureFile[0];
        }
      } else {
        delete values.signature;
      }

      const apiFunc = isCheckoutToCourtForm
        ? sourceAdapter.custodyCheckoutCourt
        : sourceAdapter.custodyCheckoutLab;

      await apiFunc(parentId, properties, values);
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Property is checked out successfully.',
      });
      return true;
    }
    return false;
  };

  return (
    <FormProvider {...methods}>
      <Stack style={{ overflowX: 'hidden' }}>
        <SideModalTitle>
          {custodyAction.split('_').join(' ').toLowerCase()}
        </SideModalTitle>
        <Grid container>
          <Grid item md={12}>
            <CustodyActionSection officerOptions={officerOptions} />
          </Grid>
          {CustodyCheckoutCourtFormFields.map(({ name, label, ...rest }) => (
            <Grid key={name} item md={12}>
              {(name === 'signature' || name === 'notes') && (
                <Divider sx={{ margin: '10px 0 20px' }} />
              )}
              <RenderFormField
                {...rest}
                name={name}
                label={
                  name === 'vendor' && !isCheckoutToCourtForm
                    ? 'Lab name'
                    : label
                }
                items={name === 'vendor' ? courtVendors : undefined}
              />
            </Grid>
          ))}
        </Grid>
        <PropertyModalFooter onSave={onCheckout} />
      </Stack>
    </FormProvider>
  );
};
